// Chamada à IA da central (api/conversar.ts), que conduz a conversa com memória e usa a SAK
// só para buscar artigos. Se falhar, quem chama volta para a SAK direto (uma pergunta por vez).

import { canalAtual } from "./metricas";

const TEMPO_LIMITE_MS = 30000; // passou disso, cai para a SAK direto
const MAX_MENSAGENS = 16;

export interface MensagemParaIA {
  papel: "usuario" | "assistente";
  texto: string;
}

export interface RespostaIA {
  registro?: number | null; // número da resposta nas métricas (null sem banco)
  modo: string;
  resposta: string;
  opcoes: string[];
  precisa_humano: boolean;
  resumo_caso: string;
  artigo_usado: { titulo: string; link: string; html?: string; video?: string | null } | null;
  artigos: { nomeartigo: string; link: string; descricao: string; video?: string }[];
}

interface MensagemGuardada {
  tipo: "usuario" | "assistente";
  texto: string;
  anexoTexto?: string;
}

// Histórico guardado -> mensagens para a IA. O texto do print vai junto da mensagem em que foi
// anexado; o marcador "pediu_ajuda" (cartão do atendente) fica de fora.
export const mensagensParaIA = (historico: MensagemGuardada[], rotuloPrint: string): MensagemParaIA[] =>
  historico
    .filter((m) => m.texto && m.texto !== "pediu_ajuda")
    .map((m) => ({
      papel: m.tipo,
      texto: m.anexoTexto ? `${m.texto}\n\n${rotuloPrint}:\n${m.anexoTexto}` : m.texto,
    }))
    .slice(-MAX_MENSAGENS);

export const perguntarIA = async (
  corpo: { slug: string; app: string; idioma: "pt" | "es"; conversa: string; mensagens: MensagemParaIA[] },
  sinal: AbortSignal
): Promise<RespostaIA> => {
  const limite = new AbortController();
  const timer = setTimeout(() => limite.abort(), TEMPO_LIMITE_MS);
  const cancelar = () => limite.abort();
  sinal.addEventListener("abort", cancelar);
  try {
    const resposta = await fetch("/api/conversar", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...corpo, ...canalAtual() }),
      signal: limite.signal,
    });
    // No `npm start` sem o servidor da função, volta o index.html: vira erro e cai na SAK.
    const json = resposta.headers.get("content-type")?.includes("application/json")
      ? await resposta.json()
      : null;
    if (!resposta.ok || typeof json?.resposta !== "string" || !json.resposta.trim()) {
      throw new Error(`conversar: ${resposta.status} ${json?.erro || ""}`);
    }
    return json as RespostaIA;
  } finally {
    clearTimeout(timer);
    sinal.removeEventListener("abort", cancelar);
  }
};
